import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { L } from "@/i18n";

const get = (url, key) => async () => (await api.get(url)).data?.[key];

export const useStudentData = (id) => {
  const [concluded, failed, exams, modules, failedCount, sessions] = useQueries({
    queries: [
      { queryKey: ["student", id, "concluded"], queryFn: get(`/admin/users/${id}/concluded`, "concluded_modules") },
      { queryKey: ["student", id, "failed"], queryFn: get(`/admin/users/${id}/failed`, "failed_modules") },
      { queryKey: ["student", id, "exams"], queryFn: get(`/admin/users/${id}/exams`, "exams") },
      { queryKey: ["student", id, "modules"], queryFn: get(`/admin/users/${id}/modules`, "allowed_modules") },
      { queryKey: ["student", id, "failed-count"], queryFn: get(`/quiz/failed/${id}/count`, "count"), retry: false },
      { queryKey: ["student", id, "sessions"], queryFn: get(`/admin/users/${id}/sessions?limit=20`, "sessions"), retry: false },
    ],
  });
  return { concluded, failed, exams, modules, failedCount, sessions };
};

export const useModuleFailedCount = (id, module) =>
  useQuery({
    queryKey: ["student", id, "failed-count", module],
    queryFn: get(`/quiz/failed/${id}/count?module_title=${encodeURIComponent(module)}`, "count"),
    enabled: !!module,
    retry: false,
  });

export const useResetProgress = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => api.post(`/admin/users/${id}/reset-progress`),
    onSuccess: (_, { id, username }) => {
      toast.success(L("{u}'s progress has been reset.", "Progresso de {u} foi reposto.", { u: username }));
      qc.invalidateQueries({ queryKey: ["student", id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(L("Failed to reset progress: {e}", "Falha ao repor progresso: {e}", { e: errorMessage(e) })),
  });
};

const useExamMutation = (fn, ok, fail) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (_, vars) => {
      toast.success(ok(vars));
      qc.invalidateQueries({ queryKey: ["student", vars.userId, "exams"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(`${fail}: ${errorMessage(e)}`),
  });
};

export const useCreateExam = () =>
  useExamMutation(
    ({ userId, exam }) => api.post(`/admin/users/${userId}/exams`, exam),
    ({ username, exam }) => L("Exam {m} scheduled for {u}.", "Exame {m} agendado para {u}.", { m: exam.module, u: username }),
    L("Failed to schedule exam", "Falha ao agendar exame"),
  );

export const useRemoveExam = () =>
  useExamMutation(
    ({ userId, examId }) => api.delete(`/admin/users/${userId}/exams/${examId}`),
    ({ username, module }) => L("Exam {m} removed from {u}.", "Exame {m} removido de {u}.", { m: module, u: username }),
    L("Failed to remove exam", "Falha ao remover exame"),
  );
